/* eslint-disable no-console */
import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  VoiceRecognitionStatus,
  UseVoiceRecognitionOptions,
  UseVoiceRecognitionReturn,
  SpeechRecognition,
  SpeechRecognitionEvent,
  SpeechRecognitionErrorEvent,
} from './types';

// Re-export types for backward compatibility
export type { VoiceRecognitionStatus, VoiceRecognitionResult, UseVoiceRecognitionOptions, UseVoiceRecognitionReturn } from './types';

// ============================================================================
// Helpers
// ============================================================================

function getErrorMessage(error: string): string {
  switch (error) {
  case 'no-speech':
    return 'No speech was detected. Please try again.';
  case 'audio-capture':
    return 'No microphone was found. Please ensure a microphone is connected.';
  case 'not-allowed':
    return 'Microphone permission was denied. Please allow microphone access.';
  case 'network':
    return 'Network error occurred. Please check your connection.';
  case 'aborted':
    return 'Speech recognition was aborted.';
  case 'language-not-supported':
    return 'The selected language is not supported.';
  case 'service-not-allowed':
    return 'Speech recognition service is not allowed.';
  default:
    return `Speech recognition error: ${error}`;
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Custom hook for voice recognition using the Web Speech API.
 * 
 * Provides functionality to listen for and transcribe speech input,
 * with support for continuous listening, interim results, and various callbacks.
 * 
 * @param options - Configuration options for voice recognition
 * @param options.language - BCP 47 language code (default: 'en-US')
 * @param options.continuous - If true, keeps listening after speech ends (default: false)
 * @param options.interimResults - If true, returns results while still speaking (default: true)
 * @param options.maxAlternatives - Maximum number of alternative transcripts (default: 1)
 * @param options.onResult - Callback fired when a result is received
 * @param options.onError - Callback fired when an error occurs
 * @param options.onEnd - Callback fired when recognition ends
 * @param options.onStart - Callback fired when recognition starts
 * 
 * @returns Object containing recognition state and control methods
 * 
 * @example
 * ```tsx
 * const { status, transcript, startListening, stopListening } = useVoiceRecognition({
 *   language: 'en-US',
 *   continuous: true,
 *   onResult: (result) => console.log('Heard:', result.transcript),
 * });
 * ```
 */
export function useVoiceRecognition(options: UseVoiceRecognitionOptions = {}): UseVoiceRecognitionReturn {
  const {
    language = 'en-US',
    continuous = false,
    interimResults = true,
    maxAlternatives = 1,
    onResult,
    onError,
    onEnd,
    onStart,
  } = options;

  const [status, setStatus] = useState<VoiceRecognitionStatus>('idle');
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const isListeningRef = useRef(false);
  const statusRef = useRef<VoiceRecognitionStatus>('idle');
  
  // Use refs for callbacks to avoid re-creating recognition on callback changes
  const onResultRef = useRef(onResult);
  const onErrorRef = useRef(onError);
  const onEndRef = useRef(onEnd);
  const onStartRef = useRef(onStart);
  
  // Update refs when callbacks change
  useEffect(() => {
    onResultRef.current = onResult;
    onErrorRef.current = onError;
    onEndRef.current = onEnd;
    onStartRef.current = onStart;
  }, [onResult, onError, onEnd, onStart]);
  
  // Keep status ref in sync
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  // Check browser support
  const isSupported = typeof window !== 'undefined' && 
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Initialize speech recognition
  useEffect(() => {
    if (!isSupported) return;

    const SpeechRecognitionClass = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognitionClass();

    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = language;
    recognition.maxAlternatives = maxAlternatives;

    recognition.onstart = () => {
      console.log('[useVoiceRecognition] onstart fired - now listening for commands');
      isListeningRef.current = true;
      setStatus('listening');
      setError(null);
      onStartRef.current?.();
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      console.log('[useVoiceRecognition] onresult fired, results:', event.results.length);
      let interim = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0].transcript;
        const confidence = result[0].confidence;
        
        console.log('[useVoiceRecognition] Transcript:', text, 'isFinal:', result.isFinal);

        if (result.isFinal) {
          setTranscript(prev => prev + text);
          onResultRef.current?.({ transcript: text, confidence, isFinal: true });
        } else {
          interim += text;
        }
      }

      setInterimTranscript(interim);
      
      if (interim) {
        onResultRef.current?.({ transcript: interim, confidence: 0, isFinal: false });
      }
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.log('[useVoiceRecognition] onerror fired:', event.error);
      
      // Ignore 'aborted' and 'no-speech' errors - they're normal during transitions
      if (event.error === 'aborted' || event.error === 'no-speech') {
        console.log('[useVoiceRecognition] Ignoring error:', event.error);
        isListeningRef.current = false;
        return;
      }
      
      const errorMessage = getErrorMessage(event.error);
      setError(errorMessage);
      setStatus('error');
      isListeningRef.current = false;
      onErrorRef.current?.(errorMessage);
    };

    recognition.onend = () => {
      console.log('[useVoiceRecognition] onend fired');
      if (isListeningRef.current && statusRef.current !== 'error') {
        setStatus('processing');
        // Brief processing state before returning to idle
        setTimeout(() => {
          setStatus(prev => prev === 'processing' ? 'idle' : prev);
        }, 500);
      }
      isListeningRef.current = false;
      onEndRef.current?.();
    };

    recognitionRef.current = recognition;

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, [isSupported, language, continuous, interimResults, maxAlternatives]);

  const startListening = useCallback(() => {
    console.log('[useVoiceRecognition] startListening called, isSupported:', isSupported, 'recognition:', !!recognitionRef.current);
    
    if (!isSupported || !recognitionRef.current) {
      console.log('[useVoiceRecognition] Not supported or no recognition ref');
      setError('Speech recognition is not supported in this browser');
      setStatus('error');
      return;
    }

    if (isListeningRef.current) {
      console.log('[useVoiceRecognition] Already listening, skipping');
      return;
    }

    setTranscript('');
    setInterimTranscript('');
    setError(null);

    try {
      console.log('[useVoiceRecognition] Starting recognition...');
      recognitionRef.current.start();
      console.log('[useVoiceRecognition] Recognition started successfully');
    } catch (err) {
      console.log('[useVoiceRecognition] Start error:', err);
      // Recognition might already be started
    }
  }, [isSupported]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current && isListeningRef.current) {
      recognitionRef.current.stop();
    }
  }, []);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setError(null);
    setStatus('idle');
  }, []);

  return {
    status,
    transcript,
    interimTranscript,
    error,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
  };
}

export default useVoiceRecognition;
